import * as React from "react"
import { cn } from "../lib/utils.ts"
import { XIcon } from "lucide-react"
import { Dialog as SheetPrimitive } from "radix-ui"

function Sheet({ ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />
}

function SheetTrigger({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Trigger>) {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
}

function SheetClose({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Close>) {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />
}

function SheetPortal({
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Portal>) {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />
}

function SheetOverlay({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Overlay>) {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-(--z-overlay) bg-backdrop motion-backdrop",
        className
      )}
      {...props}
    />
  )
}

/*
 * A side sheet: the raised surface at elevation 2. On a desktop it floats 8 px inside the
 * viewport with the 12 px overlay radius, level with the content sheet (below the installed app's
 * title bar: --sheet-inset-top); on a phone it is full bleed, below that title bar too. It slides in from its
 * edge over 320 ms on the emphasized curve and leaves in 120 ms (motion-sheet).
 */
function SheetContent({
  className,
  children,
  side = "right",
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left"
  showCloseButton?: boolean
}) {
  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        data-slot="sheet-content"
        data-side={side}
        className={cn(
          "fixed z-(--z-overlay) flex flex-col bg-popover text-popover-foreground shadow-elevation-2 outline-none motion-sheet",
          side === "right" &&
            "top-(--titlebar-inset) bottom-0 right-0 w-full border-l border-border sm:top-(--sheet-inset-top) sm:bottom-2 sm:right-2 sm:max-w-(--drawer-width) sm:overflow-hidden sm:rounded-overlay sm:border",
          side === "left" &&
            "top-(--titlebar-inset) bottom-0 left-0 w-full border-r border-border sm:top-(--sheet-inset-top) sm:bottom-2 sm:left-2 sm:max-w-(--drawer-width) sm:overflow-hidden sm:rounded-overlay sm:border",
          side === "top" &&
            "inset-x-0 top-(--titlebar-inset) h-auto border-b border-border",
          side === "bottom" &&
            "inset-x-0 bottom-0 h-auto max-h-[90vh] rounded-t-overlay border-t border-border",
          className
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <SheetPrimitive.Close className="absolute top-3 right-3 inline-flex size-control-sm items-center justify-center rounded-control text-fg-subtle transition-colors duration-(--duration-fast) hover:bg-fg/6 hover:text-fg disabled:pointer-events-none">
            <XIcon className="size-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
      </SheetPrimitive.Content>
    </SheetPortal>
  )
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1 border-b border-border-subtle px-(--drawer-padding) pt-4 pb-3", className)}
      {...props}
    />
  )
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="sheet-footer"
      className={cn("mt-auto flex items-center justify-end gap-2 border-t border-border-subtle px-(--drawer-padding) py-3", className)}
      {...props}
    />
  )
}

function SheetTitle({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Title>) {
  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn("pr-8 font-display text-title text-fg", className)}
      {...props}
    />
  )
}

function SheetDescription({
  className,
  ...props
}: React.ComponentProps<typeof SheetPrimitive.Description>) {
  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-caption text-fg-muted", className)}
      {...props}
    />
  )
}

export {
  Sheet,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
}
